import {apiConstants} from '@helper/constants';

export const QR_TRANSLATION_PLATFORM = 'qr_order';
export const QR_TRANSLATION_DEFAULT_LOCALE = 'ko';
export const QR_BUNDLED_LOCALES = [
  'en', 'ko', 'vi', 'zh_cn', 'ja', 'th', 'ne', 'es', 'de', 'fr', 'pt_br', 'ru',
];

const CACHE_SCHEMA = 1;
const CATALOG_CACHE_PREFIX = 'orderpro.ui_translation.qr_order.catalog.';
const MANIFEST_CACHE_KEY = 'orderpro.ui_translation.qr_order.manifest';

export function normalizeQrLocale(locale, fallback = QR_TRANSLATION_DEFAULT_LOCALE) {
  if (typeof locale !== 'string') return fallback;
  const normalized = locale.trim().toLowerCase().replace(/-/g, '_');
  const aliases = {cn: 'zh_cn', zh: 'zh_cn', tw: 'zh_cn', zh_tw: 'zh_cn', jp: 'ja', pt: 'pt_br'};
  const resolved = aliases[normalized] || normalized;
  return /^[a-z]{2,3}(?:_[a-z0-9]{2,8})*$/.test(resolved) ? resolved : fallback;
}

function getStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (error) {
    return null;
  }
}

function readJson(key) {
  try {
    const value = getStorage()?.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
}

function writeJson(key, value) {
  try {
    getStorage()?.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Storage can be unavailable or full. Runtime translations must remain optional.
  }
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function getCachedQrCatalog(locale) {
  const lCode = normalizeQrLocale(locale);
  const cached = readJson(CATALOG_CACHE_PREFIX + lCode);
  return cached?.schema === CACHE_SCHEMA
    && cached?.platform === QR_TRANSLATION_PLATFORM
    && cached?.l_code === lCode
    && isPlainObject(cached?.translations)
      ? cached
      : null;
}

export function getCachedQrLanguages() {
  const cached = readJson(MANIFEST_CACHE_KEY);
  const languages = cached?.schema === CACHE_SCHEMA && Array.isArray(cached?.available_languages)
    ? cached.available_languages
    : [];
  const codes = languages
    .map(language => normalizeQrLocale(language?.l_code, ''))
    .filter(Boolean);
  return codes.length ? Array.from(new Set(codes)) : QR_BUNDLED_LOCALES;
}

function cacheManifest(availableLanguages) {
  if (!Array.isArray(availableLanguages)) return;
  writeJson(MANIFEST_CACHE_KEY, {
    schema: CACHE_SCHEMA,
    platform: QR_TRANSLATION_PLATFORM,
    available_languages: availableLanguages,
    cached_at: new Date().toISOString(),
  });
}

function buildCatalogUrl(locale) {
  const url = new URL(apiConstants.translationCatalogURL, window.location.origin);
  url.searchParams.set('platform', QR_TRANSLATION_PLATFORM);
  if (locale) url.searchParams.set('l_code', normalizeQrLocale(locale));
  return url.toString();
}

async function requestCatalog(locale, {signal, timeoutMs = 5000} = {}) {
  const lCode = locale ? normalizeQrLocale(locale) : '';
  const cached = lCode ? getCachedQrCatalog(lCode) : null;
  const headers = {Accept: 'application/json'};
  if (cached?.etag) headers['If-None-Match'] = cached.etag;

  const requestController = new AbortController();
  const abortRequest = () => requestController.abort();
  if (signal?.aborted) abortRequest();
  signal?.addEventListener?.('abort', abortRequest, {once: true});
  const timeout = setTimeout(abortRequest, Math.max(1000, Number(timeoutMs) || 5000));

  let response;
  try {
    response = await fetch(buildCatalogUrl(lCode), {
      method: 'GET',
      headers,
      cache: 'no-cache',
      credentials: 'omit',
      signal: requestController.signal,
    });
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener?.('abort', abortRequest);
  }

  if (response.status === 304 && cached) {
    cacheManifest(cached.available_languages);
    return {...cached, source: 'cache'};
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    // The status below remains the useful public error for callers.
  }

  if (!response.ok || payload?.ok !== true) {
    const requestError = new Error(payload?.error?.code || 'TRANSLATION_CATALOG_FAILED');
    requestError.status = response.status;
    requestError.payload = payload;
    throw requestError;
  }

  if (payload.platform !== QR_TRANSLATION_PLATFORM || !Array.isArray(payload.available_languages)) {
    throw new Error('TRANSLATION_RESPONSE_INVALID');
  }
  cacheManifest(payload.available_languages);

  if (!lCode) return {...payload, source: 'remote'};
  if (normalizeQrLocale(payload.l_code, '') !== lCode || !isPlainObject(payload.translations)) {
    throw new Error('TRANSLATION_RESPONSE_INVALID');
  }

  const catalog = {
    schema: CACHE_SCHEMA,
    platform: QR_TRANSLATION_PLATFORM,
    l_code: lCode,
    fallback_l_code: normalizeQrLocale(payload.fallback_l_code),
    version: String(payload.version || ''),
    etag: response.headers.get('etag') || (payload.version ? `"${payload.version}"` : ''),
    translations: payload.translations,
    available_languages: payload.available_languages,
    cached_at: new Date().toISOString(),
  };
  writeJson(CATALOG_CACHE_PREFIX + lCode, catalog);
  return {...catalog, source: 'remote'};
}

export async function fetchQrTranslationCatalog(locale, options = {}) {
  try {
    return await requestCatalog(locale, options);
  } catch (error) {
    if (error?.status === 400 || error?.status === 404) throw error;
    const cached = getCachedQrCatalog(locale);
    if (cached) return {...cached, source: 'stale-cache', error};
    throw error;
  }
}

export async function fetchQrTranslationManifest(options = {}) {
  return requestCatalog('', options);
}
